/**
 * <s-product-gallery> — PDP media viewer: one large slide at a time in a
 * scroll-snap track, prev/next arrows, a "1 / N" counter, and thumbnails.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontally scrolling snap container.
 *   [data-gallery-slide]          — each slide (direct children of the track).
 *   [data-gallery-thumb="<i>"]    — thumbnail buttons (optional).
 *   [data-gallery-thumbs]         — thumbnail scroller (optional).
 *   [data-gallery-prev] / [data-gallery-next] — arrow buttons (optional).
 *   [data-gallery-current]        — element receiving the 1-based index (optional).
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.thumbScroller = this.querySelector('[data-gallery-thumbs]');
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.current = this.querySelector('[data-gallery-current]');

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.prev?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.next?.addEventListener('click', () => this.go(this.#index + 1), { signal });
    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryThumb)), { signal });
    }
    this.track.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') this.go(this.#index + (this.#rtl ? -1 : 1));
        else if (event.key === 'ArrowLeft') this.go(this.#index + (this.#rtl ? 1 : -1));
        else return;
        event.preventDefault();
      },
      { signal }
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setActive(this.slides.indexOf(entry.target));
          }
        }
      },
      { root: this.track, threshold: [0.6] }
    );
    for (const slide of this.slides) this.#observer.observe(slide);
    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  get #rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  go(index) {
    const count = this.slides.length;
    if (!count) return;
    const target = (index + count) % count;
    const slide = this.slides[target];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offset = slide.offsetLeft - this.track.offsetLeft;
    this.track.scrollTo({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(target);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    if (this.current) this.current.textContent = String(index + 1);
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        this.#revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  #revealThumb(thumb) {
    const scroller = this.thumbScroller;
    if (!scroller) return;
    const vertical = scroller.scrollHeight > scroller.clientHeight + 1 && scroller.scrollWidth <= scroller.clientWidth + 1;
    if (vertical) {
      const top = thumb.offsetTop - scroller.offsetTop - (scroller.clientHeight - thumb.offsetHeight) / 2;
      scroller.scrollTo({ top, behavior: 'smooth' });
    } else {
      const left = thumb.offsetLeft - scroller.offsetLeft - (scroller.clientWidth - thumb.offsetWidth) / 2;
      scroller.scrollTo({ left, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
